import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { getPO } from '../queries/queries';
import { ReceiptHeader } from '../common/types';

type FetchPoResponse = {
  payload?: ReceiptHeader;
  error: string;
};

type GetPOResponse = {
  getPO: string;
};

export async function fetchPODetails(poid: string, asn: string): Promise<FetchPoResponse> {
  const genericResponseError = {
    payload: undefined,
    error: 'An error occurred while fetching PO details.'
  };

  console.log('[fetchPODetails] Called with:', { poid, asn });

  try {
    // If searching by ASN (docket number), implement dual API call approach
    if (asn && !poid) {
      console.log('[fetchPODetails] ASN search detected:', asn);

      // Step 1: Call ASN API to get basic data and extract poid
      const asnSearch = { poid: null, asn: asn };
      const asnResult = (await API.graphql(graphqlOperation(getPO, asnSearch))) as GraphQLResult<GetPOResponse>;

      if (!asnResult.data || !asnResult.data.getPO) {
        return genericResponseError;
      }

      const asnPoResult = JSON.parse(asnResult.data.getPO);
      if (asnPoResult.statusCode !== 200) {
        return genericResponseError;
      }

      const asnBodyResult = JSON.parse(asnPoResult.body);
      console.log('ASN API call payload:', asnBodyResult);

      // Step 2: Extract poNumber from ASN response
      const extractedPoNumber = asnBodyResult?.poNumber;
      if (!extractedPoNumber) {
        console.error('Could not extract poNumber from ASN response');
        return {
          payload: undefined,
          error: 'Could not extract PO Number from ASN response.'
        };
      }

      console.log(`Extracted poNumber: ${extractedPoNumber}, making secondary PO API call`);

      // Step 3: Make secondary call with extracted poNumber to get complete data including shipping method
      const poSearch = { poid: extractedPoNumber, asn: null };
      const poResult = (await API.graphql(graphqlOperation(getPO, poSearch))) as GraphQLResult<GetPOResponse>;

      if (poResult.data && poResult.data.getPO) {
        const secondaryPoResult = JSON.parse(poResult.data.getPO);
        if (secondaryPoResult.statusCode === 200) {
          const secondaryBodyResult = JSON.parse(secondaryPoResult.body);
          console.log('Secondary PO API call payload:', secondaryBodyResult);

          // CRITICAL FIX: Filter lines to only include the searched ASN
          // The ASN body result should have the line(s) specific to that ASN
          const originalAsnLines = asnBodyResult?.lines || [];

          console.log('Original ASN lines from first call:', originalAsnLines);
          console.log('All PO lines from second call:', secondaryBodyResult.lines);

          // CRITICAL FIX for multiple ASN lines with same PO line:
          // When an ASN has multiple line items that reference the same PO line
          // (e.g., ASN has qty 6 + qty 4 for the same item on PO line with qty 12),
          // we need to show each ASN line as a separate receipt line.
          //
          // The ASN API returns separate lines (e.g., 963236 and 963237), but after
          // transformation, both get the same order-line ID (4445675). We need to
          // preserve each ASN line as a separate entry so users can receipt them individually.

          // Map each ASN line to its PO line data with ASN-specific quantities
          const filteredLines = originalAsnLines
            .map((asnLine: any) => {
              // Find the matching PO line by ID
              const poLine = secondaryBodyResult.lines.find((poL: any) => poL.id === asnLine.id);

              if (!poLine) {
                console.warn(`Could not find PO line for ASN line ${asnLine.id}`);
                return null;
              }

              // Return merged data: PO line details with ASN-specific quantities
              // Use asnLineId as the unique key for UI (each ASN line has a unique asnLineId from the API)
              return {
                ...poLine,
                // Use asnLineId as the unique identifier for UI rendering
                id: asnLine.asnLineId,
                // Keep the original PO line ID for Coupa receipt creation
                originalPoLineId: asnLine.id,
                // Store asnLineId explicitly for reference
                asnLineId: asnLine.asnLineId,
                // Use ASN-specific quantities
                quantityOrdered: asnLine.quantityOrdered,
                quantityReceived: asnLine.quantityReceived ?? 0,
                asnLineNum: asnLine.asnLineNum || '',
                poLineNum: asnLine.poLineNum || '',
                asnLineStatus: asnLine.asnLineStatus || ''
              };
            })
            .filter(Boolean); // Remove any null entries

          console.log(`Mapped ${originalAsnLines.length} ASN lines to PO data:`, filteredLines);

          // Return the complete PO data BUT with only the ASN-specific lines and quantities
          const filteredResult = {
            ...secondaryBodyResult,
            lines: filteredLines,
            // Preserve the ASN docket number from the original search
            docketNumber: asnBodyResult?.docketNumber || secondaryBodyResult.docketNumber
          };

          console.log('Final filtered result for ASN search:', filteredResult);
          console.log('Successfully retrieved ASN-specific data with shipping method');
          return { payload: filteredResult, error: '' };
        }
      }

      console.error('Secondary PO API call failed, falling back to ASN data');
      // If secondary call fails, return the ASN data as fallback
      return { payload: asnBodyResult, error: '' };
    }

    // Original logic for direct PO search or when both poid and asn are provided
    const search = { poid: poid || null, asn: asn || null };
    const result = (await API.graphql(graphqlOperation(getPO, search))) as GraphQLResult<GetPOResponse>;

    if (result.data && result.data.getPO) {
      const poResult = JSON.parse(result.data.getPO);
      if (poResult.statusCode === 200) {
        const bodyResult = JSON.parse(poResult.body);
        return { payload: bodyResult, error: '' };
      }
    }
    return genericResponseError;
  } catch (e) {
    console.error('Error fetching PO details: ', e);
    return genericResponseError;
  }
}
